// Client-side "has this email purchased before" check, backed by localStorage.
// No login/customer session exists in this store, so the only identity signal
// available is the email captured into `ajs_user_id` (Segment) once a visitor
// submits any form (newsletter, waitlist, checkout, etc.) — see segment-user.js.

import Cookies from 'js-cookie'
import { getEnvironment } from '@/scripts/utils/helpers'

const STORAGE_KEY = 'purchase_checks'

type PurchaseCheckMap = Record<string, boolean>

function normalizeEmail(email: string): string {
	return email.trim().toLowerCase()
}

// ajs_user_id is stored as a JSON string by Segment; unwrap it if needed.
function unwrapStoredValue(raw: string): string {
	try {
		const parsed = JSON.parse(raw)
		if (typeof parsed === 'string' && parsed) return parsed
	} catch {
		/* not JSON — treat raw value as-is */
	}
	return raw
}

function resolveEmail(email?: string): string | undefined {
	if (email) return normalizeEmail(email)

	// localStorage first — segment.liquid syncs the cookie into it on every
	// page load, so this is the common case.
	const stored = localStorage.getItem('ajs_user_id')
	if (stored && stored !== 'null') return normalizeEmail(unwrapStoredValue(stored))

	// Fall back to the cookie directly in case that sync hasn't run yet
	// (script ordering) or localStorage is unavailable/was cleared.
	const cookie = Cookies.get('ajs_user_id')
	if (cookie) return normalizeEmail(unwrapStoredValue(cookie))

    // localStorage first — storage sync this after a checkout is done
    const altStored = localStorage.getItem('_x_email')
    if (altStored) return normalizeEmail(unwrapStoredValue(altStored))

	return undefined
}

function readMap(): PurchaseCheckMap {
	try {
		const raw = localStorage.getItem(STORAGE_KEY)
		if (!raw) return {}
		const parsed = JSON.parse(raw)
		return typeof parsed === 'object' && parsed ? parsed : {}
	} catch {
		return {}
	}
}

function writeMap(map: PurchaseCheckMap): void {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
	} catch {
		/* localStorage unavailable (quota, private mode) — skip persisting */
	}
}

const inFlight = new Map<string, Promise<boolean | undefined>>()

/** Synchronous cache-only read — does not trigger a fetch. */
export function getPurchaseStatusSync(email?: string): boolean | undefined {
	const key = resolveEmail(email)
	return key ? readMap()[key] : undefined
}

/** Look up (and cache) whether `email` has purchased before. No-ops if no
 *  email is known or a cached result already exists. Dedupes concurrent
 *  calls for the same email. Failures are not cached, so a later call
 *  retries instead of permanently locking in a bad result. */
export async function checkPurchaseHistoryIfNeeded(
	email?: string
): Promise<boolean | undefined> {
	const key = resolveEmail(email)
	if (!key) return undefined

	const cached = readMap()[key]
	if (cached !== undefined && cached) return cached

	const existing = inFlight.get(key)
	if (existing) return existing

	const promise = fetch(
		`${getEnvironment().commerceBase}/v1/orders/purchased?email=${encodeURIComponent(key)}`
	)
		.then((r) => r.json())
		.then((data: { hasPurchased: boolean }) => {
			const map = readMap()
			map[key] = data.hasPurchased
			writeMap(map)
			return data.hasPurchased
		})
		.catch(() => undefined)
		.finally(() => inFlight.delete(key))

	inFlight.set(key, promise)
	return promise
}

if (typeof window !== 'undefined') {
	;(window as unknown as { PurchaseCheck?: unknown }).PurchaseCheck = {
		checkIfNeeded: checkPurchaseHistoryIfNeeded,
	}
}
